import UserService from './UserService';

export const GRAFANA_JWT_COOKIE_NAME = 'jwt';
export const GRAFANA_JWT_COOKIE_PATH = '/grafana';

export const MIN_VALIDITY_SECONDS = 60;

export const MIN_REFRESH_DELAY_MS = 5_000;

export const MAX_REFRESH_DELAY_MS = 5 * 60_000;

export const TRANSIENT_RETRY_DELAY_MS = 15_000;

let activeConsumers = 0;
let refreshTimerId = null;
let refreshInFlight = null;

const cookieSecuritySuffix = () => {
	const isSecure = window.location.protocol === 'https:';
	return `${isSecure ? 'Secure; ' : ''}SameSite=Strict`;
};

export const writeGrafanaJwtCookie = () => {
	if (!UserService.isLoggedIn()) return false;

	const token = UserService.getToken();
	if (!token || typeof token !== 'string' || token.trim() === '') {
		console.warn(
			'Grafana auth session: cookie not set because access token is missing'
		);
		return false;
	}

	document.cookie = `${GRAFANA_JWT_COOKIE_NAME}=${token}; path=${GRAFANA_JWT_COOKIE_PATH}; ${cookieSecuritySuffix()}`;
	return true;
};

export const clearGrafanaJwtCookie = () => {
	document.cookie = `${GRAFANA_JWT_COOKIE_NAME}=; path=${GRAFANA_JWT_COOKIE_PATH}; Max-Age=0; ${cookieSecuritySuffix()}`;
};

export const computeRefreshDelayMs = (
	tokenExpSec,
	nowMs = Date.now(),
	{
		minValiditySeconds = MIN_VALIDITY_SECONDS,
		minDelayMs = MIN_REFRESH_DELAY_MS,
		maxDelayMs = MAX_REFRESH_DELAY_MS,
	} = {}
) => {
	if (typeof tokenExpSec !== 'number' || !Number.isFinite(tokenExpSec)) {
		return minDelayMs;
	}

	const nowSec = nowMs / 1000;
	const msUntilRefreshNeeded = (tokenExpSec - minValiditySeconds - nowSec) * 1000;
	return Math.min(maxDelayMs, Math.max(minDelayMs, msUntilRefreshNeeded));
};

export const isAccessTokenCurrentlyValid = (nowMs = Date.now()) => {
	if (!UserService.isLoggedIn()) return false;

	const token = UserService.getToken();
	if (!token || typeof token !== 'string' || token.trim() === '') return false;

	const exp = UserService.getKC()?.tokenParsed?.exp;
	if (typeof exp !== 'number' || !Number.isFinite(exp)) return false;

	return exp * 1000 > nowMs;
};

const clearRefreshTimer = () => {
	if (refreshTimerId != null) {
		clearTimeout(refreshTimerId);
		refreshTimerId = null;
	}
};

const scheduleRefreshAfter = (delayMs) => {
	clearRefreshTimer();
	if (activeConsumers <= 0) return;

	refreshTimerId = setTimeout(() => {
		refreshTimerId = null;
		ensureGrafanaAuthSession().catch(() => {});
	}, delayMs);
};

const scheduleNextRefresh = () => {
	const tokenExpSec = UserService.getKC()?.tokenParsed?.exp;
	scheduleRefreshAfter(computeRefreshDelayMs(tokenExpSec));
};

const scheduleTransientRetry = () => {
	scheduleRefreshAfter(TRANSIENT_RETRY_DELAY_MS);
};

const handleRefreshFailure = (error) => {
	if (isAccessTokenCurrentlyValid()) {
		console.warn(
			'Grafana auth session: token refresh failed transiently; retaining jwt cookie and retrying'
		);
		if (activeConsumers > 0) {
			scheduleTransientRetry();
		}
		return;
	}

	clearGrafanaJwtCookie();
	console.warn(
		'Grafana auth session: token refresh failed and access token is unusable; cleared jwt cookie'
	);
	UserService.doLogin();
	throw error;
};

export const ensureGrafanaAuthSession = () => {
	if (refreshInFlight) return refreshInFlight;

	refreshInFlight = UserService.ensureFreshToken(MIN_VALIDITY_SECONDS)
		.then(() => {
			writeGrafanaJwtCookie();
			if (activeConsumers > 0) {
				scheduleNextRefresh();
			}
		})
		.catch((error) => handleRefreshFailure(error))
		.finally(() => {
			refreshInFlight = null;
		});

	return refreshInFlight;
};

export const acquireGrafanaAuthSession = () => {
	activeConsumers += 1;

	writeGrafanaJwtCookie();

	return ensureGrafanaAuthSession();
};

export const releaseGrafanaAuthSession = () => {
	activeConsumers = Math.max(0, activeConsumers - 1);
	if (activeConsumers === 0) {
		clearRefreshTimer();
	}
};

export const getGrafanaAuthSessionStateForTests = () => ({
	activeConsumers,
	hasTimer: refreshTimerId != null,
	hasInFlightRefresh: refreshInFlight != null,
});

export const resetGrafanaAuthSessionForTests = () => {
	activeConsumers = 0;
	clearRefreshTimer();
	refreshInFlight = null;
};
